var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import styled from 'styled-components';
import Link from './link.js';
import LoadingDots from './loading-dots.js';
const sizeVariants = {
    small: {
        padding: '0.5em 1.33em',
        fontSize: '12px',
        borderRadius: '12px',
    },
    medium: {
        padding: '0.64em 1.71em',
        fontSize: '14px',
        borderRadius: '12px',
    },
    large: {
        padding: '0.75em 2em',
        fontSize: '16px',
        borderRadius: '12px',
    },
    extraLarge: {
        padding: '0.9em 1.6em',
        fontSize: '20px',
        borderRadius: '16px',
    },
    cardButton: {
        padding: '8px',
        fontSize: '16px',
        borderRadius: '12px',
    },
};
const getSize = ({ size = 'medium' }) => {
    var _a;
    return (_a = sizeVariants[size]) !== null && _a !== void 0 ? _a : sizeVariants.medium;
};
const styleVariation = {
    main: {
        borderColor: 'transparent',
        backgroundColor: ({ theme }) => theme.azure,
        /* Imposes a background radial gradient too small to detect, that upon activation will expand 100% of the container like a ripple from the click */
        background: ({ theme }) => `${theme.bubbleBlue400} radial-gradient(circle, transparent 1%, ${theme.bubbleBlue300} 1%) center/15000%`,
        /*
          Despite remaining the same, the font color must be defined
          for both hover/focus so as to override nested link styling
        */
        color: ({ theme }) => theme.white,
        hover: {
            backgroundColor: ({ theme }) => theme.bubbleBlue400,
            color: ({ theme }) => theme.white,
        },
        focus: {
            backgroundColor: ({ theme }) => theme.bubbleBlue400,
            color: ({ theme }) => theme.white,
        },
        active: {
            /* Tied to the background property, these will create the click impression effect */
            backgroundSize: '100%',
            transition: 'background 0s',
        },
    },
    outline: {
        borderColor: ({ theme }) => theme.azure,
        backgroundColor: ({ theme }) => theme.transparentWhite,
        /* Imposes a background radial gradient too small to detect, that upon activation will expand 100% of the container like a ripple from the click */
        background: ({ theme }) => `${theme.bubbleBlue100} radial-gradient(circle, transparent 1%, ${theme.bubbleBlue200} 1%) center/15000%`,
        color: ({ theme }) => theme.azure,
        hover: {
            backgroundColor: ({ theme }) => theme.bubbleBlue100,
        },
        focus: {
            backgroundColor: ({ theme }) => theme.bubbleBlue100,
        },
        active: {
            backgroundColor: ({ theme }) => theme.bubbleBlue200,
            /* Tied to the background property, these will create the click impression effect */
            backgroundSize: '100%',
            transition: 'background 0s',
        },
    },
    buttonLink: {
        borderColor: 'transparent',
        backgroundColor: ({ theme }) => theme.transparentWhite,
        color: ({ theme }) => theme.bubbleBlue500,
        padding: 0,
        hover: {
            borderColor: 'transparent',
            color: ({ theme }) => theme.oceanBoat,
        },
        focus: {
            borderColor: 'transparent',
            color: ({ theme }) => theme.oceanBoat,
        },
        active: {
            borderColor: 'transparent',
            color: ({ theme }) => theme.oceanBoat,
        },
    },
    noOutline: {
        borderColor: 'transparent',
        backgroundColor: ({ theme }) => theme.transparentWhite,
        color: ({ theme }) => theme.bubbleBlue500,
        hover: {
            borderColor: ({ theme }) => theme.bubbleBlue400,
        },
        focus: {
            borderColor: ({ theme }) => theme.bubbleBlue400,
        },
        active: {
            backgroundColor: ({ theme }) => theme.bubbleBlue200,
            borderColor: 'transparent',
        },
    },
    whiteOutline: {
        borderColor: ({ theme }) => theme.white,
        backgroundColor: ({ theme }) => theme.transparentWhite,
        color: ({ theme }) => theme.white,
        hover: {
            backgroundColor: ({ theme }) => theme.bubbleBlue100,
            color: ({ theme }) => theme.azure,
        },
        focus: {
            backgroundColor: ({ theme }) => theme.bubbleBlue100,
            color: ({ theme }) => theme.azure,
        },
        active: {
            backgroundColor: ({ theme }) => theme.bubbleBlue200,
            color: ({ theme }) => theme.azure,
            borderColor: 'transparent',
        },
    },
};
const getVariation = ({ variation = 'main' }) => {
    var _a;
    return (_a = styleVariation[variation]) !== null && _a !== void 0 ? _a : styleVariation.main;
};
const getRule = (rulePath) => ({ size, variation, theme }) => {
    const rules = Object.assign(Object.assign({}, getSize({ size })), getVariation({ variation }));
    const rule = rulePath.split('.').reduce((rules, pathPart) => { var _a; return (_a = rules[pathPart]) !== null && _a !== void 0 ? _a : ''; }, rules);
    return typeof rule === 'function' ? rule({ theme }) : rule;
};
const getDisabledStyleByVariation = ({ variation, theme }) => {
    switch (variation) {
        case 'buttonLink':
        case 'noOutline':
            return '';
        case 'outline':
            return `border-color: ${theme.neutral300};`;
        default:
            return `background-color: ${theme.neutral200};`;
    }
};
const StyledButton = styled.button `
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  font-family: postgrotesk, Roboto, Helvetica, sans-serif;
  padding: ${getRule('padding')};
  border: 1px solid ${getRule('borderColor')};
  border-radius: ${getRule('borderRadius')};
  background: ${getRule('background')};
  background-color: ${getRule('backgroundColor')};
  color: ${getRule('color')};
  font-size: ${getRule('fontSize')};
  line-height: 1;
  text-align: center;
  white-space: ${({ wrap }) => (wrap ? 'normal' : 'nowrap')};
  cursor: pointer;
  outline: none;
  font-weight: 700;
  transition: ease-out background 0.4s;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  &:hover:not([disabled]) {
    background-color: ${getRule('hover.backgroundColor')};
    color: ${getRule('hover.color')};
    border-color: ${getRule('hover.borderColor')};
  }

  /* Override hover styles, b/c when pressed the hover event is also active */
  &:hover:active {
    background-color: ${getRule('active.backgroundColor')};
    color: ${getRule('active.color')};
    border-color: ${getRule('active.borderColor')};
  }

  &:focus {
    background-color: ${getRule('focus.backgroundColor')};
    color: ${getRule('focus.color')};
    border-color: ${getRule('focus.borderColor')};
  }

  &[disabled] {
    ${({ isLoading, variation = 'main', theme }) => isLoading
    ? ''
    : `
    ${getDisabledStyleByVariation({ variation, theme })}
    color: ${theme.neutral400};
    cursor: not-allowed;
  `}
  }

  &:active {
    background-color: ${getRule('active.backgroundColor')};
    background-size: ${getRule('active.backgroundSize')};
    transition: ${getRule('active.transition')};
  }

  & ${LoadingDots} {
    color: ${({ variation = 'main', theme }) => (variation === 'main' ? theme.white : theme.azure)};
  }
`;
const ButtonBase = (_a) => {
    var { children, isLoading, onClick = () => { } } = _a, props = __rest(_a, ["children", "isLoading", "onClick"]);
    return (_jsx(StyledButton, Object.assign({ disabled: isLoading, onClick: (event) => {
            event.persist();
            onClick(event);
        } }, props, { children: isLoading ? _jsx(LoadingDots, { "data-testid": "button-loading-dots" }) : children })));
};
ButtonBase.displayName = 'ButtonBase';
const ButtonLink = styled(StyledButton).attrs(() => ({ as: Link })) `
  text-decoration: none;
`;
ButtonLink.displayName = 'ButtonLink';
const ButtonAnchor = styled(StyledButton).attrs(() => ({ as: 'a' })) `
  text-decoration: none;
`;
ButtonAnchor.displayName = 'ButtonAnchor';
export const Button = (props) => {
    if (!props.href) {
        return _jsx(ButtonBase, Object.assign({}, props));
    }
    if (props.asAnchor) {
        return _jsx(ButtonAnchor, Object.assign({}, props));
    }
    return _jsx(ButtonLink, Object.assign({}, props));
};
Button.displayName = 'Button';
const StyledButtonExp = styled(Button).withConfig({ displayName: "brc-StyledButtonExp", componentId: "brc-159sye8" }) ``;
export default StyledButtonExp;
//# sourceMappingURL=button.js.map